import "./polyfills/matches";

export function toNode(node: string | HTMLElement): HTMLElement {
	if (typeof node === "string") {
		node = (document.getElementById(node) || document.querySelector(node)) as HTMLElement;
	}
	return node || document.body;
}

type eventPrepare = (ev:Event) => any;
interface IHandlerHash {
	[name: string]: ((...args: any[]) => (boolean | void));
}

export function eventHandler(prepare:eventPrepare, hash:IHandlerHash){
	const keys = Object.keys(hash);

	return function(ev:Event){
		const data = prepare(ev);
		let node = ev.target as (HTMLElement | SVGElement);

		while (node){
			const cssstring =  node.getAttribute ? (node.getAttribute("class") || "") : "";
			if (cssstring.length){
				const css = cssstring.split(" ");
				for (let j=0; j<keys.length; j++){
					if (css.indexOf(keys[j]) > -1){
						return hash[keys[j]](ev, data);
					}
				}
			}
			node = node.parentNode as (HTMLElement | SVGElement);
		}

		return true;
	};
}

export function locate(target: Event | Element, attr: string = "dhx_id"): string {
	const node = locateNode(target, attr);
	return node ? node.getAttribute(attr) : "";
}
export function locateNode(target: Event | Element, attr: string = "dhx_id", dir: string = "target"): Element {
	if (target instanceof Event) {
		target = target[dir] as HTMLElement;
	}
	while (target) {
		if (target.getAttribute && target.getAttribute(attr)) {
			return target;
		}
		target = target.parentNode as HTMLElement;
	}
}

export function getBox(elem) {
	const box = elem.getBoundingClientRect();
	const body = document.body;

	const scrollTop = window.pageYOffset || body.scrollTop;
	const scrollLeft = window.pageXOffset || body.scrollLeft;

	const top = box.top + scrollTop;
	const left = box.left + scrollLeft;
	const right = body.offsetWidth - box.right;
	const bottom = body.offsetHeight - box.bottom;
	const width = box.right - box.left;
	const height = box.bottom - box.top;

	return { top, left, right, bottom, width, height };
}

let scrollWidth = -1;
export function getScrollbarWidth(): number {
	if (scrollWidth > -1){
		return scrollWidth;
	}

	const scrollDiv = document.createElement("div");
	document.body.appendChild(scrollDiv);
	scrollDiv.style.cssText = "position: absolute;left: -99999px;overflow:scroll;width: 100px;height: 100px;";
	scrollWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
	document.body.removeChild(scrollDiv);
	return scrollWidth;
}

export interface IFitTarget {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface IFitPosition {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

export interface IFitPositionConfig {
	mode?: Position;
	auto?: boolean;
	centering?: boolean;
	width: number;
	height: number;
}

export function fitPosition(node: HTMLElement, config: IFitPositionConfig) {
	return calculatePosition(getRealPosition(node), config);
}

export function isIE() {
	const ua = window.navigator.userAgent;
	return ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
}

export function getRealPosition(node: HTMLElement): IFitPosition {
	const rects = node.getBoundingClientRect();
	return {
		left: rects.left + window.pageXOffset,
		right: rects.right + window.pageXOffset,
		top: rects.top + window.pageYOffset,
		bottom: rects.bottom + window.pageYOffset
	};
}

export enum Position {
	left = "left",
	right = "right",
	bottom = "bottom",
	top = "top"
}


export function calculatePosition(pos: IFitPosition, config: IFitPositionConfig) {
	const {left, top} = config.mode === Position.bottom || config.mode === Position.top
		? placeBottomOrTop(pos, config)
		: placeRightOrLeft(pos, config);
	return {
		left: Math.round(left) + "px",
		top: Math.round(top) + "px",
		minWidth: Math.round(config.width) + "px",
		position: "absolute"
	};
}

function getWindowBorders() {
	return {
		rightBorder: window.pageXOffset + window.innerWidth,
		bottomBorder: window.pageYOffset + window.innerHeight
	};
}

function horizontalCentering(pos: IFitPosition, width: number, rightBorder: number) {
	const nodeWidth = pos.right - pos.left;
	const diff = (width - nodeWidth) / 2;

	const left = pos.left - diff;
	const right = pos.right + diff;

	if (left >= 0 && right <= rightBorder) {
		return left;
	}

	if (left < 0) {
		return 0;
	}

	return rightBorder - width;
}

function verticalCentering(pos: IFitPosition, height: number, bottomBorder: number) {
	const nodeHeight = pos.bottom - pos.top;
	const diff = (height - nodeHeight) / 2;

	const top = pos.top - diff;
	const bottom = pos.bottom + diff;

	if (top >= 0 && bottom <= bottomBorder) {
		return top;
	}

	if (top < 0) {
		return 0;
	}

	return bottomBorder - height;
}

function placeBottomOrTop(pos: IFitPosition, config: IFitPositionConfig) {
	const {rightBorder, bottomBorder} = getWindowBorders();

	let left;
	let top;

	const bottomDiff = bottomBorder - pos.bottom - config.height;
	const topDiff = pos.top - config.height;

	if (config.mode === Position.bottom) {
		if (bottomDiff >= 0) {
			top = pos.bottom;
		} else if (topDiff >= 0) {
			top = topDiff;
		}
	} else {
		if (topDiff >= 0) {
			top = topDiff;
		} else if (bottomDiff >= 0) {
			top = pos.bottom;
		}
	}

	if (bottomDiff < 0 && topDiff < 0) {
		if (config.auto) {
			return placeRightOrLeft(pos, {...config, mode: Position.right, auto: false});
		}
		top = bottomDiff > topDiff ? pos.bottom : topDiff;
	}

	if (config.centering) {
		left = horizontalCentering(pos, config.width, rightBorder);
	} else {
		const leftDiff = rightBorder - pos.left - config.width;
		const rightDiff = pos.right - config.width;

		if (leftDiff >= 0) {
			left = pos.left;
		} else if (rightDiff >= 0) {
			left = rightDiff;
		} else {
			left = rightDiff > leftDiff  ? pos.left : rightDiff;
		}
	}

	return {left, top};
}

function placeRightOrLeft(pos: IFitPosition, config: IFitPositionConfig) {
	const {rightBorder, bottomBorder} = getWindowBorders();

	let left;
	let top;

	const rightDiff = rightBorder - pos.right - config.width;
	const leftDiff = pos.left - config.width;

	if (config.mode === Position.right) {
		if (rightDiff >= 0) {
			left = pos.right;
		} else if (leftDiff >= 0) {
			left = leftDiff;
		}
	} else {
		if (leftDiff >= 0) {
			left = leftDiff;
		} else if (rightDiff >= 0) {
			left = pos.right;
		}
	}

	if (leftDiff < 0 && rightDiff < 0) {
		if (config.auto) {
			return placeBottomOrTop(pos, {...config, mode: Position.bottom, auto: false});
		}
		left = leftDiff > rightDiff ? leftDiff : pos.right;
	}

	if (config.centering) {
		top = verticalCentering(pos, config.height, rightBorder);
	} else {
		const bottomDiff = pos.bottom - config.height;
		const topDiff = bottomBorder - pos.top - config.height;

		if (topDiff >= 0) {
			top = pos.top;
		} else if (bottomDiff > 0) {
			top = bottomDiff;
		} else {
			top = bottomDiff > topDiff  ? bottomDiff : pos.top;
		}
	}

	return {left, top};
}